/**
 * Gráfico de Barras para Ajuste al Cargo
 * Muestra la distribución de candidatos por nivel de ajuste (Alto, Medio, Bajo)
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import Card from '@mui/material/Card';

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const JobFitBarChart = ({ data, title = "Nivel de Ajuste al Cargo" }) => {
  // Si no hay datos, mostrar mensaje
  if (!data || data.total === 0) {
    return (
      <Card>
        <MDBox p={3}>
          <MDTypography variant="h6" fontWeight="medium">
            {title}
          </MDTypography>
          <MDBox mt={3} textAlign="center">
            <MDTypography variant="body2" color="text">
              Sin registros disponibles
            </MDTypography>
          </MDBox>
        </MDBox>
      </Card>
    );
  }

  // Configuración de datos del gráfico
  const chartData = {
    labels: ['Ajuste Alto', 'Ajuste Medio', 'Ajuste Bajo'],
    datasets: [
      {
        label: 'Número de Candidatos',
        data: [data.high || 0, data.medium || 0, data.low || 0],
        backgroundColor: [
          'rgba(76, 175, 80, 0.7)',   // Verde - Alto
          'rgba(255, 193, 7, 0.7)',   // Amarillo - Medio
          'rgba(244, 67, 54, 0.7)',   // Rojo - Bajo
        ],
        borderColor: [
          'rgba(76, 175, 80, 1)',
          'rgba(255, 193, 7, 1)',
          'rgba(244, 67, 54, 1)',
        ],
        borderWidth: 2,
        borderRadius: 8,
        barThickness: 60,
      },
    ],
  };

  // Opciones del gráfico
  const options = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold',
        },
        bodyFont: {
          size: 13,
        },
        callbacks: {
          label: (context) => {
            const value = context.parsed.y || 0;
            const percentage = ((value / data.total) * 100).toFixed(1);
            return `Candidatos: ${value} (${percentage}%)`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          font: {
            size: 12,
          },
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 12,
            weight: '500',
          },
        },
      },
    },
  };

  return (
    <Card>
      <MDBox p={3}>
        <MDTypography variant="h6" fontWeight="medium" mb={2}>
          {title}
        </MDTypography>
        
        {/* Estadísticas rápidas */}
        <MDBox mb={3} display="flex" justifyContent="space-around">
          <MDBox textAlign="center">
            <MDTypography variant="h4" fontWeight="bold" color="success">
              {data.high}
            </MDTypography>
            <MDTypography variant="caption" color="text">
              Alto ({data.percentages?.high || 0}%)
            </MDTypography>
          </MDBox>
          <MDBox textAlign="center">
            <MDTypography variant="h4" fontWeight="bold" color="warning">
              {data.medium}
            </MDTypography>
            <MDTypography variant="caption" color="text">
              Medio ({data.percentages?.medium || 0}%)
            </MDTypography>
          </MDBox>
          <MDBox textAlign="center">
            <MDTypography variant="h4" fontWeight="bold" color="error">
              {data.low}
            </MDTypography>
            <MDTypography variant="caption" color="text">
              Bajo ({data.percentages?.low || 0}%)
            </MDTypography>
          </MDBox>
        </MDBox>

        {/* Gráfico de barras */}
        <MDBox sx={{ maxWidth: '600px', margin: '0 auto', minHeight: '300px' }}>
          <Bar data={chartData} options={options} />
        </MDBox>

        {/* Información adicional */}
        <MDBox mt={3} p={2} borderRadius="lg" bgColor="grey-100">
          <MDTypography variant="body2" color="text" textAlign="center">
            <strong>Total evaluados:</strong> {data.total} candidatos
          </MDTypography>
          {data.high > 0 && (
            <MDTypography variant="caption" color="success" textAlign="center" display="block" mt={1}>
              ✓ {data.high} candidatos con alto nivel de ajuste al cargo
            </MDTypography>
          )}
        </MDBox>
      </MDBox>
    </Card>
  );
};

JobFitBarChart.propTypes = {
  data: PropTypes.shape({
    high: PropTypes.number.isRequired,
    medium: PropTypes.number.isRequired,
    low: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    percentages: PropTypes.shape({
      high: PropTypes.number,
      medium: PropTypes.number,
      low: PropTypes.number,
    }),
  }).isRequired,
  title: PropTypes.string,
};

export default JobFitBarChart;
